import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Makes #anchor links work on a cold page load.
 *
 * On a fresh load the browser tries to jump to the anchor while React has only
 * rendered an empty root div, finds nothing, and gives up — so a pasted link
 * like /teamleaders#copy-assist lands at the top of the page instead.
 *
 * We capture the incoming hash at module load (before React renders) and hold it
 * until we've successfully scrolled to it. Anchor clicks made *inside* the app
 * are left completely alone, so they keep their native CSS smooth scroll.
 *
 * Note: the pending target lives at module scope rather than in a ref because
 * StrictMode double-invokes effects in development — a ref guard gets consumed
 * by the first (discarded) run and the real one then skips.
 */
let pendingId = (() => {
  if (typeof window === "undefined") return null;
  const h = window.location.hash;
  return h && h.length > 1 ? decodeURIComponent(h.slice(1)) : null;
})();

export default function ScrollToHash() {
  const { hash, pathname } = useLocation();

  useEffect(() => {
    if (!pendingId) return;
    const id = pendingId;
    // Visitor navigated elsewhere before we got to it — hand back to the browser.
    if (decodeURIComponent((hash || "").slice(1)) !== id) {
      pendingId = null;
      return;
    }

    let cancelled = false;
    let lastSetY = null;
    const timers = [];
    // If they start scrolling themselves, stop yanking the page around.
    const cancel = () => { cancelled = true; };
    const opts = { passive: true };
    window.addEventListener("wheel", cancel, opts);
    window.addEventListener("touchstart", cancel, opts);
    window.addEventListener("keydown", cancel, opts);

    const jump = () => {
      const el = document.getElementById(id);
      if (!el) return false;
      // If the page has moved since our last correction, the visitor moved it —
      // stop correcting. More reliable than input events alone, and covers
      // wheel, touch, keyboard and the scrollbar equally.
      if (lastSetY !== null && Math.abs(window.scrollY - lastSetY) > 40) {
        cancelled = true;
        return true;
      }
      // Instant, not smooth — a cold load can be thousands of pixels away.
      // scroll-margin-top on the target handles the sticky nav offset.
      el.scrollIntoView({ behavior: "auto", block: "start" });
      lastSetY = window.scrollY;
      return true;
    };

    const started = Date.now();
    const poll = setInterval(() => {
      if (cancelled) return clearInterval(poll);
      if (jump()) {
        clearInterval(poll);
        pendingId = null;
        // Fetched content (demo samples, images, embeds) changes page height
        // after mount, so re-settle a few times.
        [200, 600, 1200].forEach((d) =>
          timers.push(setTimeout(() => { if (!cancelled) jump(); }, d)),
        );
        return;
      }
      if (Date.now() - started > 5000) {
        clearInterval(poll);
        pendingId = null;
      }
    }, 80);

    return () => {
      cancelled = true;
      clearInterval(poll);
      timers.forEach(clearTimeout);
      window.removeEventListener("wheel", cancel);
      window.removeEventListener("touchstart", cancel);
      window.removeEventListener("keydown", cancel);
    };
  }, [hash, pathname]);

  return null;
}
